import { unzipSync, strFromU8 } from 'fflate';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { assessDocumentTextQuality } from './document-text-quality';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

function xmlText(xml: string): string {
  return xml
    .replace(/<w:tab\s*\/?>/g, '\t')
    .replace(/<w:br\s*\/?>|<a:br\s*\/?>/g, '\n')
    .replace(/<\/w:p>|<\/a:p>|<\/row>/g, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/[ \t]+/g, ' ').replace(/\n\s+/g, '\n').trim();
}

export async function extractPdfText(file: File): Promise<{ text: string; pages: number; reliableNativeText: boolean }> {
  const loadingTask = getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const pdf = await loadingTask.promise;
  const sections: string[] = [];
  const pageReliability: boolean[] = [];
  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const text = content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text) {
        sections.push(`--- หน้า ${pageNumber} ---\n${text}`);
        pageReliability.push(assessDocumentTextQuality(text).reliable);
      } else {
        pageReliability.push(false);
      }
      page.cleanup();
    }
    return {
      text: sections.join('\n\n'),
      pages: pdf.numPages,
      reliableNativeText: pageReliability.length === pdf.numPages && pageReliability.every(Boolean),
    };
  } finally {
    await loadingTask.destroy();
  }
}

export async function extractOfficeText(file: File): Promise<string> {
  const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  const names = Object.keys(archive).filter((name) => {
    if (extension === 'docx') return name === 'word/document.xml' || /^word\/(header|footer)\d+\.xml$/.test(name);
    if (extension === 'pptx') return /^ppt\/(slides\/slide\d+|notesSlides\/notesSlide\d+)\.xml$/.test(name);
    if (extension === 'xlsx') return name === 'xl/sharedStrings.xml' || /^xl\/worksheets\/sheet\d+\.xml$/.test(name);
    return false;
  }).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return names.map((name) => xmlText(strFromU8(archive[name]))).filter(Boolean).join('\n\n').slice(0, 2_000_000);
}
